/**
 * Runway helper — shared math for "how many months of runway does this
 * founder have right now?". Used by both the Dashboard RunwayCard (KPI
 * tile) and the Self-funded panel's lazy initialisers (next iteration).
 *
 * Priority chain (mirrors the backend `_effective_monthly_burn` /
 * `_effective_startup_total` ordering so the panel always agrees with
 * the dashboard):
 *
 *   Starting savings:
 *     1. business.budget.startup_costs total (sum of actual_amount → amount)
 *     2. wizard personal_investment
 *     3. $10,000 fallback
 *
 *   Monthly burn:
 *     1. business.budget.monthly_burn total (sum of actual_amount → amount)
 *     2. Y1 expected_revenue / 24, $500 floor
 *
 *   Monthly revenue:
 *     1. Highest-year revenue_projection.actual_amount / 12
 *     2. 0
 *
 *   Net burn   = max(0, monthly_burn − monthly_revenue)
 *   Runway     = net_burn === 0  →  null (infinite, profitable)
 *                else  savings / net_burn  (months, float)
 *
 * The card surfaces `months` (or "∞" when net burn is zero) and a
 * status colour band: green ≥ 6, amber 3-6, red < 3.
 */

import { sweatBurnRelief } from "./investorMath";


const STARTING_SAVINGS_FALLBACK = 10000;

function sumLines(rows) {
  return (rows || []).reduce(
    (sum, ln) =>
      sum +
      (Number(
        ln && (ln.actual_amount != null ? ln.actual_amount : ln.amount),
      ) || 0),
    0,
  );
}

/**
 * Standard amortized monthly loan payment.
 *   M = P·r / (1 − (1+r)^−n)   where r = APR/100/12, n = term in months.
 * 0% APR degrades to straight-line P/n. Returns 0 when principal or term
 * are missing/non-positive (so a loan without term/APR adds no debt service).
 */
export function loanMonthlyPayment(principal, apr, termMonths) {
  const P = Number(principal) || 0;
  const n = Number(termMonths) || 0;
  if (P <= 0 || n <= 0) return 0;
  const r = (Number(apr) || 0) / 100 / 12;
  if (r <= 0) return P / n;
  return (P * r) / (1 - Math.pow(1 + r, -n));
}

/**
 * Pure runway calculation given a business doc. Returns:
 *   {
 *     months: number|null,      // null === infinite (net burn ≤ 0)
 *     net_burn: number,
 *     savings: number,
 *     monthly_burn: number,
 *     monthly_revenue: number,
 *     sources: { savings: string, burn: string, revenue: string },
 *     status: "ok"|"watch"|"critical"|"infinite"|"unknown",
 *   }
 * If the business has no plan at all, returns `{ status: "unknown" }`.
 */
export function calcRunway(business) {
  if (!business || !business.plan) {
    return { status: "unknown" };
  }

  const q = business.questionnaire || {};
  const budget = business.budget || {};
  const plan = business.plan || {};

  // ---- Starting savings ----
  //
  // Feb 21 2026 — Priority chain RESTRUCTURED so the founder's
  // personal investment is the source-of-truth. Previously the Budget's
  // startup_costs total was treated as "starting savings" which is
  // wrong semantically (startup costs are EXPENSES) and meant a founder
  // who saved their personal_investment value via the wizard would see
  // it ignored on the Dashboard.
  //
  //   1. wizard `personal_investment` (the founder's cash)
  //   2. Budget · startup_costs total (legacy fallback)
  //   3. $10,000 fallback
  //
  // Additive contributions on top of the base:
  //   • Any investor_scenarios with status === "funded" contribute
  //     their `ask_amount_usd` (cash already wired).
  //   • Any saved_grants with status === "awarded" contribute their
  //     `awarded_amount_usd` (cash already disbursed or scheduled).
  //
  // The base source label is preserved for hint/debugging; each
  // additive lane is also exposed on the return value so the UI can
  // render a transparent capital-stack breakdown.
  const startupSum = sumLines(budget.startup_costs);
  const wizardInvestment = Number(q.personal_investment || 0);
  let baseSavings;
  let savingsSource;
  if (wizardInvestment > 0) {
    baseSavings = wizardInvestment;
    savingsSource = "wizard";
  } else if (startupSum > 0) {
    baseSavings = startupSum;
    savingsSource = "budget";
  } else {
    baseSavings = STARTING_SAVINGS_FALLBACK;
    savingsSource = "fallback";
  }
  const fundedInvestorCapital = (business.investor_scenarios || [])
    .filter((s) => s && s.status === "funded")
    .reduce((acc, s) => acc + (Number(s.ask_amount_usd) || 0), 0);
  // Jun 2026 — Sweat equity. A funded work-in-kind commitment puts NO cash in
  // the bank; it takes a cost off the table instead, and only for as long as
  // the commitment runs. Folded into burn below, never into savings.
  const sweatRelief = sweatBurnRelief(business.investor_scenarios || []);
  // Feb 21 2026 — Two-lane policy for awarded grants:
  //   • A grant WITHOUT a monthly disbursement schedule (lump sum) folds
  //     its full awarded amount into Starting Savings.
  //   • A grant WITH `disbursement_monthly_amount > 0` is treated as
  //     monthly revenue (reduces net burn) rather than front-loading the
  //     lump sum — accurate cash-flow modelling for multi-year grants
  //     (SBIR Phase II etc).
  //
  // Helper: explicit awarded_amount_usd, falling back to the grants.gov
  // range midpoint so the win shows up the instant the founder flips
  // status, even before they've keyed in the confirmed amount.
  const _grantAmount = (g) => {
    const explicit = Number(g.awarded_amount_usd) || 0;
    if (explicit > 0) return explicit;
    const lo = Number(g.amount_min) || 0;
    const hi = Number(g.amount_max) || 0;
    if (hi > 0) return lo > 0 ? (lo + hi) / 2 : hi;
    return 0;
  };
  const awardedGrants = (business.grants || []).filter(
    (g) => g && g.status === "awarded",
  );
  const awardedGrantCapital = awardedGrants
    .filter((g) => !(Number(g.disbursement_monthly_amount) > 0))
    .reduce((acc, g) => acc + _grantAmount(g), 0);
  // Jul 2026 — Total committed value of EVERY awarded grant (lump sum OR the
  // full scheduled disbursement), independent of the capital-vs-revenue runway
  // split. Powers the Funding Mix chart so a multi-year/monthly grant still
  // shows as a funding source instead of silently vanishing from the mix.
  const _grantTotalValue = (g) => {
    const explicit = Number(g.awarded_amount_usd) || 0;
    if (explicit > 0) return explicit;
    const monthly = Number(g.disbursement_monthly_amount) || 0;
    const months = Number(g.disbursement_months) || 0;
    if (monthly > 0 && months > 0) return monthly * months;
    return _grantAmount(g);
  };
  const awardedGrantTotal = awardedGrants.reduce(
    (acc, g) => acc + _grantTotalValue(g), 0,
  );
  const grantMonthlyRevenue = awardedGrants
    .filter((g) => Number(g.disbursement_monthly_amount) > 0)
    .reduce((acc, g) => acc + (Number(g.disbursement_monthly_amount) || 0), 0);
  // Jun 2026 — CRM closed-won deals are cash already in the bank → a savings
  // lane (modelled exactly like a lump-sum grant / funded investor capital).
  // Weighted open pipeline is surfaced separately as an informational
  // projection only — never folded into the hard runway number.
  const crmRollup = business.crm_rollup || {};
  const crmWonCapital = Number(crmRollup.won_value || 0);
  const crmProjectedRevenue = Number(crmRollup.weighted_pipeline || 0);
  // Jun 2026 — Funded loans (debt financing) are proceeds in the bank → a
  // savings lane, modelled like funded investor capital. Only loans the
  // founder has marked `funded` count toward runway.
  const fundedLoanCapital = (business.loans || [])
    .filter((l) => l && l.status === "funded")
    .reduce((acc, l) => acc + (Number(l.amount_usd) || 0), 0);
  const savings = baseSavings + fundedInvestorCapital + awardedGrantCapital + crmWonCapital + fundedLoanCapital;
  const suffixBits = [];
  if (fundedInvestorCapital > 0) suffixBits.push("investor");
  if (awardedGrantCapital > 0) suffixBits.push("grants");
  if (crmWonCapital > 0) suffixBits.push("sales");
  if (fundedLoanCapital > 0) suffixBits.push("loans");
  if (suffixBits.length) {
    // Tag the source so the UI can render "Personal + Funded investor capital + Grants"
    // instead of just "Personal".
    savingsSource = savingsSource + "+" + suffixBits.join("+");
  }

  // ---- Monthly burn ----
  const burnSum = sumLines(budget.monthly_burn);
  let monthlyBurn;
  let burnSource;
  if (burnSum > 0) {
    monthlyBurn = burnSum;
    burnSource = "budget";
  } else {
    const y1 = Number(q.expected_revenue || 0);
    monthlyBurn = Math.max(Math.round(y1 / 24), 500);
    burnSource = "heuristic";
  }
  // Jun 2026 — contributed work reduces the cost base while it runs. Floored
  // at zero: a commitment can't make the business cost less than nothing.
  const grossMonthlyBurn = monthlyBurn;
  const sweatMonthlyRelief = Math.min(grossMonthlyBurn, Math.round(sweatRelief.monthly_usd));
  if (sweatMonthlyRelief > 0) {
    monthlyBurn = grossMonthlyBurn - sweatMonthlyRelief;
    burnSource = burnSource + "-sweat";
  }

  // ---- Funded-loan debt service (amortized monthly repayment) ----
  // Jun 2026 — funded loans add cash to savings (above) but must be repaid;
  // fold the estimated monthly payment into net burn so runway is honest.
  // Only loans with a term (and any APR, incl. 0%) contribute a payment.
  const fundedLoanDebtService = Math.round(
    (business.loans || [])
      .filter((l) => l && l.status === "funded")
      .reduce((acc, l) => acc + loanMonthlyPayment(l.amount_usd, l.apr, l.term_months), 0),
  );

  // ---- Monthly revenue ----
  //
  // Feb 2026 — three possible sources, in order of precedence:
  //
  //   1. ``business.budget_revenue_actuals[latestMonth]`` — per-month
  //      actuals captured the same way as monthly burn actuals. Most
  //      authoritative because it's a real top-line for a real month.
  //   2. Annual revenue actuals on the plan (``revenue_projection``)
  //      divided by 12 — the calculated value, "actuals".
  //   3. The Budget · monthly_revenue list (mirrors monthly_burn shape):
  //      per-row actual_amount → planned amount, summed — the
  //      founder-entered template / planned figure.
  //
  // When BOTH the calculated (#2) AND budget template (#3) are present
  // we use their average. The per-month actuals (#1), when present,
  // win outright because they're the freshest, most concrete signal.
  // When none of the three are present, fall back to 0.
  const annualActual = (plan.revenue_projection || [])
    .filter((r) => r && r.actual_amount != null)
    .map((r) => ({ year: Number(r.year) || 0, amount: Number(r.actual_amount) }))
    .sort((a, b) => b.year - a.year)[0];
  const calculatedRevenue = annualActual
    ? Math.round(annualActual.amount / 12)
    : 0;
  const budgetRevenue = sumLines(budget.monthly_revenue);

  // Per-month revenue actuals — take the latest non-empty month and sum
  // its category values.
  const revenueActualsByMonth = business.budget_revenue_actuals || {};
  const sortedMonths = Object.keys(revenueActualsByMonth)
    .filter((k) => revenueActualsByMonth[k] && Object.keys(revenueActualsByMonth[k]).length > 0)
    .sort();
  const latestMonth = sortedMonths.length
    ? sortedMonths[sortedMonths.length - 1]
    : null;
  const monthlyActualsTotal = latestMonth
    ? Object.values(revenueActualsByMonth[latestMonth]).reduce(
        (a, v) => a + (Number(v) || 0),
        0,
      )
    : 0;

  let monthlyRevenue;
  let revenueSource;
  if (monthlyActualsTotal > 0) {
    monthlyRevenue = Math.round(monthlyActualsTotal);
    revenueSource = "monthly_actuals";
  } else if (calculatedRevenue > 0 && budgetRevenue > 0) {
    monthlyRevenue = Math.round((calculatedRevenue + budgetRevenue) / 2);
    revenueSource = "average";
  } else if (budgetRevenue > 0) {
    monthlyRevenue = budgetRevenue;
    revenueSource = "budget";
  } else if (calculatedRevenue > 0) {
    monthlyRevenue = calculatedRevenue;
    revenueSource = "actuals";
  } else {
    monthlyRevenue = 0;
    revenueSource = "zero";
  }
  // Feb 21 2026 — Grant monthly disbursements stack on top of the base
  // monthly revenue (multi-year grants like SBIR Phase II reduce net
  // burn but aren't "earned revenue" in the traditional sense).
  const baseMonthlyRevenue = monthlyRevenue;
  monthlyRevenue = baseMonthlyRevenue + grantMonthlyRevenue;
  if (grantMonthlyRevenue > 0) {
    revenueSource = revenueSource + "+grants";
  }

  // Oct 2026 — Fiscal-sponsor admin fee: a nonprofit operating under a fiscal
  // sponsor loses a % of funds raised to the sponsor. Fold that into the
  // runway math so monthly funding (and therefore net burn / runway) reflects
  // NET funds, not gross. Only applies when a fee is set (nonprofit-only field).
  const monthlyRevenueGross = monthlyRevenue;
  const sponsorFeePct = Math.min(100, Math.max(0, Number(business.nonprofit_formation?.fiscal_sponsor_fee_pct) || 0));
  let sponsorFeeMonthly = 0;
  if (sponsorFeePct > 0 && monthlyRevenue > 0) {
    sponsorFeeMonthly = Math.round(monthlyRevenue * (sponsorFeePct / 100));
    monthlyRevenue = monthlyRevenue - sponsorFeeMonthly;
    revenueSource = revenueSource + "+net_sponsor_fee";
  }

  // ---- Runway ----
  const netBurn = Math.max(0, monthlyBurn + fundedLoanDebtService - monthlyRevenue);
  const months = netBurn > 0 ? savings / netBurn : null;

  let status;
  if (months === null) status = "infinite";
  else if (months >= 6) status = "ok";
  else if (months >= 3) status = "watch";
  else status = "critical";

  return {
    months,
    net_burn: netBurn,
    savings,
    sponsor_fee_pct: sponsorFeePct,
    sponsor_fee_monthly: sponsorFeeMonthly,
    monthly_revenue_gross: monthlyRevenueGross,
    // Feb 2026 — split savings into its three legs so callers can render
    // a transparent breakdown without re-running the priority chain.
    base_savings: baseSavings,
    funded_investor_capital: fundedInvestorCapital,
    // Jun 2026 — Sweat equity: monthly cost relief from funded work-in-kind
    // commitments still running, with a per-commitment breakdown so the UI
    // can show the lane rather than a silent discount.
    sweat_monthly_relief: sweatMonthlyRelief,
    sweat_lanes: sweatRelief.lanes,
    monthly_burn_gross: grossMonthlyBurn,
    awarded_grant_capital: awardedGrantCapital,
    // Jul 2026 — Total committed award value (lump + scheduled) for the Funding
    // Mix chart; see `_grantTotalValue`.
    awarded_grant_total: awardedGrantTotal,
    // Jun 2026 — CRM closed-won cash folded into savings; weighted open
    // pipeline exposed as an informational projection (not in the hard number).
    crm_won_capital: crmWonCapital,
    crm_projected_revenue: crmProjectedRevenue,
    // Jun 2026 — Funded loan (debt) proceeds folded into savings.
    funded_loan_capital: fundedLoanCapital,
    // Jun 2026 — Estimated monthly repayment on funded loans (term+APR),
    // folded into net burn so runway reflects debt service.
    monthly_debt_service: fundedLoanDebtService,
    // Feb 21 2026 — Grant monthly disbursements modelled as a revenue
    // lane (reduces net burn). Exposed separately so the UI can render
    // "$X from grants, monthly" vs lump-sum capital.
    grant_monthly_revenue: grantMonthlyRevenue,
    monthly_burn: monthlyBurn,
    monthly_revenue: monthlyRevenue,
    monthly_revenue_base: baseMonthlyRevenue,
    // Feb 2026 — Budget UI needs the calculated value alone to pre-fill
    // the new Monthly Revenue input. Surface both legs so callers don't
    // have to recompute.
    monthly_revenue_calculated: calculatedRevenue,
    monthly_revenue_budget: budgetRevenue,
    // Per-month actuals breakdown — exposed so the Monthly Revenue panel
    // can show "from May 2026 actuals" provenance under the input.
    monthly_revenue_actuals_total: monthlyActualsTotal,
    monthly_revenue_actuals_month: latestMonth,
    sources: {
      savings: savingsSource,
      burn: burnSource,
      revenue: revenueSource,
    },
    status,
  };
}

export const RUNWAY_COLOR = {
  ok: "#059669",
  watch: "#F59E0B",
  critical: "#DC2626",
  infinite: "#059669",
  unknown: "#9CA3AF",
};

export const RUNWAY_LABEL = {
  ok: "Solid",
  watch: "Watch",
  critical: "Critical",
  infinite: "Profitable",
  unknown: "—",
};


// Jul 2026 · Upcoming scheduled grant cash, month by month. Walks every AWARDED
// grant with a monthly disbursement schedule and buckets the committed amount
// into the next `monthsAhead` calendar months (from `now`). Grants with an
// explicit `disbursement_start_date` begin that month; otherwise they start now.
// Returns [{ key:'YYYY-MM', label:'Mon YY', amount }] for months that have cash,
// capped at `monthsAhead`, plus `total`.
export function upcomingGrantDisbursements(grants, { monthsAhead = 12, now = new Date() } = {}) {
  const base = new Date(now.getFullYear(), now.getMonth(), 1);
  const buckets = new Map(); // offset -> { amount, contributors: [{grantId, monthKey, amount, received}] }
  for (const g of grants || []) {
    if ((g?.status || "") !== "awarded") continue;
    const monthly = Number(g?.disbursement_monthly_amount) || 0;
    const months = Number(g?.disbursement_months) || 0;
    if (monthly <= 0 || months <= 0) continue;
    const startRaw = g?.disbursement_start_date ? new Date(g.disbursement_start_date) : base;
    const start = Number.isNaN(startRaw.getTime()) ? base : new Date(startRaw.getFullYear(), startRaw.getMonth(), 1);
    const startOffset = (start.getFullYear() - base.getFullYear()) * 12 + (start.getMonth() - base.getMonth());
    const receivedSet = new Set(Array.isArray(g?.received_disbursement_months) ? g.received_disbursement_months : []);
    for (let m = 0; m < months; m += 1) {
      const offset = startOffset + m;
      if (offset < 0 || offset >= monthsAhead) continue;
      const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const b = buckets.get(offset) || { amount: 0, contributors: [] };
      b.amount += monthly;
      b.contributors.push({ grantId: g.id, monthKey, amount: monthly, received: receivedSet.has(monthKey) });
      buckets.set(offset, b);
    }
  }
  const rows = [];
  let total = 0;
  for (let offset = 0; offset < monthsAhead; offset += 1) {
    const b = buckets.get(offset);
    if (!b || b.amount <= 0) continue;
    const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
    rows.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      amount: b.amount,
      received: b.contributors.every((c) => c.received),
      contributors: b.contributors,
    });
    total += b.amount;
  }
  return { rows, total };
}

// Jun 2026 · Grant Cash Alerts — scheduled grant disbursements whose month is
// already in the PAST and which the founder has NOT ticked off as received.
// `amount` on each row is only the still-unreceived portion of that month, so
// a partially-received month shows just what's missing. Walks up to
// `monthsBack` months behind `now`. Returns { rows, total, count }.
export function overdueGrantDisbursements(grants, { monthsBack = 36, now = new Date() } = {}) {
  const base = new Date(now.getFullYear(), now.getMonth(), 1);
  const buckets = new Map(); // offset(<0) -> { amount, contributors }
  for (const g of grants || []) {
    if ((g?.status || "") !== "awarded") continue;
    const monthly = Number(g?.disbursement_monthly_amount) || 0;
    const months = Number(g?.disbursement_months) || 0;
    if (monthly <= 0 || months <= 0) continue;
    const startRaw = g?.disbursement_start_date ? new Date(g.disbursement_start_date) : base;
    const start = Number.isNaN(startRaw.getTime()) ? base : new Date(startRaw.getFullYear(), startRaw.getMonth(), 1);
    const startOffset = (start.getFullYear() - base.getFullYear()) * 12 + (start.getMonth() - base.getMonth());
    const receivedSet = new Set(Array.isArray(g?.received_disbursement_months) ? g.received_disbursement_months : []);
    for (let m = 0; m < months; m += 1) {
      const offset = startOffset + m;
      if (offset >= 0 || offset < -monthsBack) continue;
      const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (receivedSet.has(monthKey)) continue; // already received → not overdue
      const b = buckets.get(offset) || { amount: 0, contributors: [] };
      b.amount += monthly;
      b.contributors.push({ grantId: g.id, monthKey, amount: monthly, received: false });
      buckets.set(offset, b);
    }
  }
  const rows = [];
  let total = 0;
  const offsets = [...buckets.keys()].sort((a, b) => a - b);
  for (const offset of offsets) {
    const b = buckets.get(offset);
    if (!b || b.amount <= 0) continue;
    const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
    rows.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      amount: b.amount,
      received: false,
      contributors: b.contributors,
      monthsAgo: -offset,
    });
    total += b.amount;
  }
  return { rows, total, count: rows.length };
}

// Jun 2026 · Funded-investor cash landings for the Runway projection chart.
// Each funded scenario's money landed on `funded_at` (falls back to
// `investment_start_date`). Returns [{ id, name, amount, date, monthOffset,
// pinnedToStart }] where monthOffset is clamped into [0, horizon] — past
// landings pin to the chart start (monthOffset 0, pinnedToStart true) and
// keep their real date in `date` for the label.
export function fundedInvestorLandings(scenarios, { horizon = 24, now = new Date() } = {}) {
  const baseY = now.getFullYear();
  const baseM = now.getMonth(); // 0-based
  const out = [];
  for (const s of scenarios || []) {
    if ((s?.status || "") !== "funded") continue;
    const raw = s?.funded_at || s?.investment_start_date;
    if (!raw) continue;
    const parts = String(raw).split("-");
    const y = Number(parts[0]);
    const mo = Number(parts[1]);
    if (!y || !mo) continue;
    const rawOffset = (y - baseY) * 12 + (mo - 1 - baseM);
    const monthOffset = Math.max(0, Math.min(horizon, rawOffset));
    out.push({
      id: s.id,
      name: s.investor_name || s.headline || "Investor",
      amount: Number(s.ask_amount_usd) || 0,
      date: raw,
      monthOffset,
      pinnedToStart: rawOffset < 0,
    });
  }
  return out.sort((a, b) => a.monthOffset - b.monthOffset);
}

// Jun 2026 · Per-month grant cash inflow for the runway chart. Returns an array
// of length `months + 1` where index i is the total scheduled grant
// disbursement landing in month offset i (0 = current month). Reuses
// `upcomingGrantDisbursements` so the numbers match the Funding Mix strip.
export function grantMonthlyInflows(grants, { months = 24, now = new Date() } = {}) {
  const base = new Date(now.getFullYear(), now.getMonth(), 1);
  const { rows } = upcomingGrantDisbursements(grants, { monthsAhead: months + 1, now });
  const arr = new Array(months + 1).fill(0);
  for (const r of rows) {
    const [y, m] = r.key.split("-").map(Number);
    const offset = (y - base.getFullYear()) * 12 + (m - 1 - base.getMonth());
    if (offset >= 0 && offset <= months) arr[offset] += r.amount;
  }
  return arr;
}
